import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { Option } from "@/types/Common";
import { normalizeText } from "@/utils/normalizeText";
import { Portal } from "@radix-ui/react-portal";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

interface FilteredSelectProps {
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
  options: Option[];
  onEnter?: (raw: string) => void;
  onSelect?: () => void;
  textEmpty?: string;
}

export function FilteredSelect({
  value,
  onChange,
  placeholder,
  options,
  onEnter,
  onSelect,
  textEmpty = "Procure ou selecione uma opção proxima",
}: FilteredSelectProps) {
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const commandListRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  useEffect(() => {
    if (value) {
      const selectedOption = options.find((o) => o.value === value);
      if (!isFocused) {
        setInputValue(selectedOption ? selectedOption.label : value);
      }
    } else if (!isFocused) {
      setInputValue("");
    }
  }, [value, options, isFocused]);

  const filtered = useMemo(() => {
    const inputNormalized = normalizeText(inputValue);
    return options
      .filter((o) => normalizeText(o.label).includes(inputNormalized))
      .slice(0, 300);
  }, [options, inputValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (newValue === "") {
      onChange("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onEnter?.(inputValue);
    }
  };

  const handleSelectOption = (optionValue: string) => {
    const selectedOption = options.find((o) => o.value === optionValue);
    if (selectedOption) {
      onChange(selectedOption.value);
      setInputValue(selectedOption.label);
    }
    setIsFocused(false);
    inputRef.current?.blur();
    onSelect?.();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInputValue("");
    onChange("");
    inputRef.current?.focus();
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isFocused && commandListRef.current && containerRef.current) {
        updateDropdownPosition();
      }
    };

    const updateDropdownPosition = () => {
      if (commandListRef.current && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        commandListRef.current.style.width = `${rect.width}px`;
        commandListRef.current.style.left = `${rect.left}px`;
        commandListRef.current.style.top = `${rect.bottom + 4}px`;
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", updateDropdownPosition);

    if (isFocused) {
      updateDropdownPosition();
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [isFocused]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        commandListRef.current &&
        !commandListRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    if (isFocused) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFocused]);

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          placeholder={placeholder}
          className="w-full pr-16"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded-full hover:bg-muted"
            >
              <X className="h-4 w-4 text-muted-foreground" />
              <span className="sr-only">Limpar</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setIsFocused((prev) => {
                const next = !prev;
                if (next) {
                  inputRef.current?.focus();
                } else {
                  inputRef.current?.blur();
                }
                return next;
              });
            }}
            className="p-1 rounded-full hover:bg-muted"
          >
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
            <span className="sr-only">
              {isFocused ? "Fechar opções" : "Abrir opções"}
            </span>
          </button>
        </div>
      </div>

      {isFocused && (
        <Portal>
          <div
            ref={commandListRef}
            className={cn(
              "fixed z-50 bg-popover text-popover-foreground shadow-md rounded-md overflow-hidden",
              isDesktop ? "max-h-[300px]" : "max-h-[200px]",
            )}
            style={{
              width: containerRef.current?.getBoundingClientRect().width + "px",
              top:
                (containerRef.current?.getBoundingClientRect().bottom || 0) +
                4 +
                "px",
              left: containerRef.current?.getBoundingClientRect().left + "px",
            }}
          >
            <Command>
              <CommandList className="max-h-full overflow-auto">
                <CommandEmpty>
                  <div className="px-4 text-xs text-n-text-neutral">
                    Nenhum resultado na lista. <br />
                    {textEmpty}
                  </div>
                </CommandEmpty>

                <CommandGroup>
                  {filtered.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => handleSelectOption(option.value)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === option.value ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        </Portal>
      )}
    </div>
  );
}
